import React, { useState, useMemo } from 'react';
import { useAppDispatch } from 'redux/store';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';

import { fetchFileEventInstances } from '../../../../redux/slices/fileEventInstancesSlice';
import { clearFileEventInstance } from '../../../../redux/slices/eventsSlice';
import { RootState } from '../../../../redux/store';

const FileEventInstances = () => {
  // See note in EventInstances.tsx — defaults preserve the `string` type
  // without a non-null assertion at the fetch call site.
  const { zone_name = '', name = '' } = useParams<{
    zone_name: string;
    name: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [showSidebar, setShowSidebar] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const instanceId = searchParams.get('instance_id') || undefined;

  const {
    items: instances,
    currentPage,
    totalPages,
    totalItems,
    status,
    error,
    config: headerConfig,
    filtersConfig,
  } = useSelector((state: RootState) => state.fileEventInstances);

  const userData = useSelector((state: RootState) => state.auth.userData);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updateUserSettings,
    updatePage,
    updateLimit,
    limit,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
    triggerActionRefetch,
  } = useTableController({
    userData,
    pageName: 'file-event-instances',
    filtersConfig,
    headerConfig,
    fetchData: (payload: any) =>
      fetchFileEventInstances({
        ...payload,
        zoneName: zone_name,
        eventName: name,
        file_event_instance_id: instanceId,
      }),
    columnFormats: { created_at: 'datetime' },
    getFormattedRow: (row: any) => ({
      ...row,
      processed: row.processed === null || row.processed === undefined ? 'null' : String(row.processed),
      ssh_connection_status_updates:
        Array.isArray(row.ssh_connection_status_updates) && row.ssh_connection_status_updates.length > 0
          ? row.ssh_connection_status_updates
              .slice(-3)
              .map((x: any) => `${x?.status || 'unknown'}@${x?.timestamp || ''}`)
              .join(' | ')
          : '[]',
    }),
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const instance = row.original;
      const disabled = instance.processed === 'true' || instance.processed === true;

      return (
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            dispatch(
              clearFileEventInstance({
                file_event_instance_id: instance.file_event_instance_id,
              }) as any
            ).then(() => {
              triggerActionRefetch();
            });
          }}
        >
          Clear
        </Button>
      );
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(instances), [instances]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <div className="d-flex align-items-center mb-3 flex-shrink-0">
        <Button variant="link" onClick={() => navigate('/automation/events')} className="p-0 me-3 text-decoration-none">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Events
        </Button>
        <h4 className="mb-0">
          File Event Instances for <code>{name}</code>
        </h4>
      </div>
      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName="file-event-instances"
        columns={(headerConfig ?? []).map((c: any) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        isLoading={status === 'loading'}
        isSilentRefresh={isSilentRefresh}
        onRefresh={refresh}
        onRowClick={(row) => {
          navigate(
            `/automation/node_instances?node_instance_id=${row.created_node_instance_id}&parentOnly=true&sortColumn=node_instance_id&sortOrder=desc&page=1&limit=100`
          );
        }}
      />
    </div>
  );
};

export default FileEventInstances;
